import { datePickerValueToString, stringToDatePickerValue } from '@civilgrid/ui/components';
import type { DateTime } from 'luxon';
import type { PermitDraft, PermitFormDataPayload } from '~/api/projects2';
import type { Project } from '~/common/types';
import { emptyContractor, MAX_CONTRACTORS, type PermittingFormValues } from './permittingFormTypes';

/** Project payload for permitting defaults (PM may include `phone` when API exposes it). */
export type PermittingProjectSource = Project;

function luxonToDatePickerValue(dt: DateTime | undefined | null) {
  if (!dt?.isValid) return null;
  return stringToDatePickerValue(dt.toISO() ?? undefined);
}

function projectManagerPhone(p: Project): string {
  const pm = p.projectManager as Project['projectManager'] & { phone?: string | null };
  return typeof pm.phone === 'string' ? pm.phone.trim() : '';
}

export function getBasePermittingDefaults(): PermittingFormValues {
  return {
    permitSubmissionDate: stringToDatePickerValue(new Date().toISOString()),
    permitSubmissionContactName: '',
    permitSubmissionContactEmail: '',
    permitSubmissionContactPhone: '',
    workStartDate: null,
    workEndDate: null,
    projectNumber: '',
    address: '',
    city: '',
    zip: '',
    atmosRep: '',
    atmosEmail: '',
    atmosPhone: '',
    workTypeText: '',
    workTypeRadio: 'main',
    contractors: [emptyContractor()],
    mapOnSeparatePage: false,
  };
}

/**
 * Prefill from the project. `permitSubmissionDate` is not on the project model — it stays “today” from
 * `getBasePermittingDefaults()` at merge time.
 */
export function buildPermittingDefaultsFromProject(p: PermittingProjectSource): Partial<PermittingFormValues> {
  const street = (p.address || '').trim() || (p.address2?.street || '').trim();
  const pm = p.projectManager;

  return {
    permitSubmissionContactName: (pm?.name || '').trim(),
    permitSubmissionContactEmail: (pm?.email || '').trim(),
    permitSubmissionContactPhone: projectManagerPhone(p),
    workStartDate: luxonToDatePickerValue(p.constructionStartDate),
    workEndDate: luxonToDatePickerValue(p.constructionEndDate),
    address: street,
    city: (p.address2?.city || '').trim(),
    zip: (p.address2?.zipCode || '').trim(),
  };
}

export function mergePermittingFormDefaults(project: PermittingProjectSource | null): PermittingFormValues {
  return {
    ...getBasePermittingDefaults(),
    ...(project ? buildPermittingDefaultsFromProject(project) : {}),
  };
}

/** Converts the react-hook-form values into the API payload shape (dates → ISO strings). */
export function formValuesToPermitFormData(form: PermittingFormValues): PermitFormDataPayload {
  return {
    permitSubmissionDate: datePickerValueToString(form.permitSubmissionDate) || null,
    permitSubmissionContactName: form.permitSubmissionContactName.trim(),
    permitSubmissionContactEmail: form.permitSubmissionContactEmail.trim(),
    permitSubmissionContactPhone: form.permitSubmissionContactPhone.trim(),
    workStartDate: datePickerValueToString(form.workStartDate) || null,
    workEndDate: datePickerValueToString(form.workEndDate) || null,
    projectNumber: form.projectNumber.trim(),
    address: form.address.trim(),
    city: form.city.trim(),
    zip: form.zip.trim(),
    atmosRep: form.atmosRep.trim(),
    atmosEmail: form.atmosEmail.trim(),
    atmosPhone: form.atmosPhone.trim(),
    workTypeText: form.workTypeText.trim(),
    workTypeRadio: form.workTypeRadio,
    mapOnSeparatePage: form.mapOnSeparatePage,
  };
}

/**
 * Builds form values from a saved draft, layered over the project defaults so any field absent from the
 * draft still gets a sensible default (and legacy transitional fields stay populated).
 */
export function permitDraftToFormValues(
  draft: Pick<PermitDraft, 'formData' | 'contractors'>,
  project: PermittingProjectSource | null,
): PermittingFormValues {
  const base = mergePermittingFormDefaults(project);
  const fd = draft.formData;
  const contractors = draft.contractors.length ? draft.contractors.slice(0, MAX_CONTRACTORS) : [emptyContractor()];

  return {
    ...base,
    permitSubmissionDate: stringToDatePickerValue(fd.permitSubmissionDate ?? undefined),
    permitSubmissionContactName: fd.permitSubmissionContactName,
    permitSubmissionContactEmail: fd.permitSubmissionContactEmail,
    permitSubmissionContactPhone: fd.permitSubmissionContactPhone,
    workStartDate: stringToDatePickerValue(fd.workStartDate ?? undefined),
    workEndDate: stringToDatePickerValue(fd.workEndDate ?? undefined),
    projectNumber: fd.projectNumber ?? '',
    address: fd.address,
    city: fd.city,
    zip: fd.zip,
    atmosRep: fd.atmosRep,
    atmosEmail: fd.atmosEmail,
    atmosPhone: fd.atmosPhone,
    workTypeText: fd.workTypeText,
    workTypeRadio: fd.workTypeRadio,
    mapOnSeparatePage: fd.mapOnSeparatePage,
    contractors,
  };
}
